import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Play,
  Pause,
  Eye,
  Loader2,
  Calendar,
  Beaker,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import { EvalsSubNav } from './EvalsSubNav';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { listEvals, startEvalRun, deleteEval } from '@/services/evalApi';
import { EvalWizard } from './EvalWizard';

interface OptimizationSummary {
  enabled: boolean;
  status: string;
  current_iteration: number;
  max_iterations: number;
  best_score: number | null;
  baseline_score: number | null;
  improvement_percent: number | null;
  pending_checkpoint: boolean;
}

interface ScheduleInfo {
  enabled: boolean;
  cron_expression: string | null;
  next_run_at: string | null;
  last_run_status: string | null;
}

interface EvalItem {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  agent: string;
  variants_count?: number;
  runs_count?: number;
  last_run?: string;
  last_run_pass_rate?: number;
  optimization?: OptimizationSummary | null;
  schedule?: ScheduleInfo | null;
}

type FilterMode = 'all' | 'standard' | 'optimization';

export function EvalList() {
  const navigate = useNavigate();
  const { registerPageContext } = useGlobalAssistant();
  const [evals, setEvals] = useState<EvalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningEval, setRunningEval] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [scheduledOnly, setScheduledOnly] = useState(false);

  // Register eval list context for global assistant
  useEffect(() => {
    registerPageContext({
      contextType: 'evals',
      evals: {
        eval_count: evals.length,
        eval_names: evals.map((e) => e.name),
      },
    });
  }, [registerPageContext, evals]);

  const fetchEvals = async () => {
    try {
      setLoading(true);
      const data = await listEvals();
      setEvals(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch evals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvals();
  }, []);

  // Filter evals based on mode and schedule
  const filteredEvals = useMemo(() => {
    return evals.filter((evalItem) => {
      // Filter by mode
      if (filterMode === 'standard' && evalItem.optimization?.enabled) {
        return false;
      }
      if (filterMode === 'optimization' && !evalItem.optimization?.enabled) {
        return false;
      }
      // Filter by scheduled
      if (scheduledOnly && !evalItem.schedule?.enabled) {
        return false;
      }
      return true;
    });
  }, [evals, filterMode, scheduledOnly]);

  // Group evals by type for hybrid view
  const groupedEvals = useMemo(() => {
    if (filterMode !== 'all') return null;

    const standard = filteredEvals.filter((e) => !e.optimization?.enabled);
    const optimization = filteredEvals.filter((e) => e.optimization?.enabled);

    return { standard, optimization };
  }, [filteredEvals, filterMode]);

  const handleRun = async (evalItem: EvalItem) => {
    try {
      setRunningEval(evalItem.id);
      await startEvalRun(evalItem.id, {});
      navigate(`/evals/${evalItem.id}`);
    } catch (err) {
      setError(
        `Failed to start run: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setRunningEval(null);
    }
  };

  const handleDelete = async (evalId: string) => {
    if (!confirm('Are you sure you want to delete this evaluation?')) return;
    try {
      await deleteEval(evalId);
      fetchEvals();
    } catch (err) {
      setError(
        `Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderEvalCard = (evalItem: EvalItem) => {
    const isOptimization = evalItem.optimization?.enabled;
    const opt = evalItem.optimization;

    return (
      <Card
        key={evalItem.id}
        className="hover:border-primary/50 transition-colors cursor-pointer"
        onClick={() => navigate(`/evals/${evalItem.id}`)}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {isOptimization ? (
                <Sparkles className="h-4 w-4 text-purple-500" />
              ) : (
                <Beaker className="h-4 w-4 text-blue-500" />
              )}
              <h3 className="font-semibold text-lg">{evalItem.name}</h3>
              {evalItem.schedule?.enabled && (
                <Badge variant="outline" className="text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  Scheduled
                </Badge>
              )}
            </div>
            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {isOptimization ? (
                <OptimizationActions
                  evalItem={evalItem}
                  onNavigate={() => navigate(`/evals/${evalItem.id}`)}
                />
              ) : (
                <StandardActions
                  evalItem={evalItem}
                  running={runningEval === evalItem.id}
                  onRun={() => handleRun(evalItem)}
                  onNavigate={() => navigate(`/evals/${evalItem.id}`)}
                />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/evals/${evalItem.id}`)}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => handleDelete(evalItem.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-3">
            Agent:{' '}
            <Badge variant="secondary">
              {evalItem.agent?.split('/').pop()?.replace('.md', '') || 'Not set'}
            </Badge>
          </div>

          {isOptimization ? (
            <OptimizationInfo opt={opt!} />
          ) : (
            <StandardInfo evalItem={evalItem} />
          )}

          {/* Pending checkpoint alert */}
          {opt?.pending_checkpoint && (
            <div className="mt-3 flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-950/30 rounded px-3 py-2 text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span>Checkpoint pending review</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <EvalsSubNav />
      <div className="flex h-[calc(100vh-7rem)]">
        <div className="flex-1 overflow-auto">
          <div className="container max-w-6xl py-8">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-3xl font-bold">Evaluations</h1>
              <div className="flex gap-2">
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Eval
                </Button>
                <Button variant="outline" onClick={fetchEvals}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Filter Controls */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <Button
                  variant={filterMode === 'all' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterMode('all')}
                >
                  All
                </Button>
                <Button
                  variant={filterMode === 'standard' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterMode('standard')}
                  className="flex items-center gap-1"
                >
                  <Beaker className="h-3.5 w-3.5" />
                  Standard
                </Button>
                <Button
                  variant={filterMode === 'optimization' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterMode('optimization')}
                  className="flex items-center gap-1"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Optimization
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="scheduled-only"
                  checked={scheduledOnly}
                  onCheckedChange={(checked) =>
                    setScheduledOnly(checked === true)
                  }
                />
                <label
                  htmlFor="scheduled-only"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Scheduled only
                </label>
              </div>
            </div>

            {/* Eval Cards */}
            {filteredEvals.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No evaluations found.</p>
                <p className="text-sm mt-1">
                  Click "Create Eval" to get started.
                </p>
              </div>
            ) : filterMode === 'all' && groupedEvals ? (
              <>
                {/* Hybrid view with sections */}
                {groupedEvals.optimization.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-500" />
                      Optimization Evals
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2">
                      {groupedEvals.optimization.map(renderEvalCard)}
                    </div>
                  </div>
                )}
                {groupedEvals.standard.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Beaker className="h-5 w-5 text-blue-500" />
                      Standard Evals
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2">
                      {groupedEvals.standard.map(renderEvalCard)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredEvals.map(renderEvalCard)}
              </div>
            )}
          </div>
        </div>
      </div>

      <EvalWizard
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={fetchEvals}
      />
    </>
  );
}

// Sub-components for cleaner rendering

function OptimizationActions({
  evalItem,
  onNavigate,
}: {
  evalItem: EvalItem;
  onNavigate: () => void;
}) {
  const status = evalItem.optimization?.status;

  if (status === 'running') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm">
            <Pause className="h-4 w-4 mr-1" />
            Pause
          </Button>
        </TooltipTrigger>
        <TooltipContent>Pause optimization</TooltipContent>
      </Tooltip>
    );
  }

  if (status === 'paused' || status === 'checkpoint_pending') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="default" size="sm" onClick={onNavigate}>
            {status === 'checkpoint_pending' ? 'Review' : 'Resume'}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {status === 'checkpoint_pending'
            ? 'Review checkpoint'
            : 'Resume optimization'}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (status === 'not_started') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="default" size="sm" onClick={onNavigate}>
            <Play className="h-4 w-4 mr-1" />
            Start
          </Button>
        </TooltipTrigger>
        <TooltipContent>Start optimization</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={onNavigate}>
      <Eye className="h-4 w-4 mr-1" />
      View
    </Button>
  );
}

function StandardActions({
  evalItem,
  running,
  onRun,
  onNavigate,
}: {
  evalItem: EvalItem;
  running: boolean;
  onRun: () => void;
  onNavigate: () => void;
}) {
  const hasVariants = (evalItem.variants_count || 0) > 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="default"
          size="sm"
          onClick={hasVariants ? onRun : onNavigate}
          disabled={running}
        >
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : hasVariants ? (
            <>
              <Play className="h-4 w-4 mr-1" />
              Run
            </>
          ) : (
            'Configure'
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {hasVariants ? 'Run evaluation' : 'Add variants to run'}
      </TooltipContent>
    </Tooltip>
  );
}

function OptimizationInfo({ opt }: { opt: OptimizationSummary }) {
  const statusIcon = {
    not_started: <Clock className="h-4 w-4 text-muted-foreground" />,
    running: <Loader2 className="h-4 w-4 animate-spin text-blue-500" />,
    paused: <Pause className="h-4 w-4 text-amber-500" />,
    checkpoint_pending: <AlertTriangle className="h-4 w-4 text-amber-500" />,
    completed: <CheckCircle2 className="h-4 w-4 text-green-500" />,
    failed: <XCircle className="h-4 w-4 text-red-500" />,
  };

  const statusLabels: Record<string, string> = {
    not_started: 'Ready to start',
    running: 'Running',
    paused: 'Paused',
    checkpoint_pending: 'Checkpoint pending',
    completed: 'Completed',
    failed: 'Failed',
  };

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        {statusIcon[opt.status as keyof typeof statusIcon] || statusIcon.not_started}
        <span>
          {opt.status === 'running' || opt.status === 'paused'
            ? `Iteration ${opt.current_iteration}/${opt.max_iterations}`
            : statusLabels[opt.status] || opt.status}
        </span>
      </div>

      {opt.best_score !== null && (
        <div className="flex items-center gap-4">
          <span>
            Best: <strong>{opt.best_score.toFixed(2)}</strong>
          </span>
          {opt.improvement_percent !== null && (
            <Badge
              variant={opt.improvement_percent > 0 ? 'default' : 'secondary'}
              className={
                opt.improvement_percent > 0
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : ''
              }
            >
              {opt.improvement_percent > 0 ? '+' : ''}
              {opt.improvement_percent.toFixed(1)}% from baseline
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

function StandardInfo({ evalItem }: { evalItem: EvalItem }) {
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      <span>
        {evalItem.variants_count || 0} variant
        {evalItem.variants_count !== 1 ? 's' : ''}
      </span>
      <span className="text-muted-foreground/50">·</span>
      {evalItem.last_run ? (
        <>
          <span>Last run: {formatRelativeTime(evalItem.last_run)}</span>
          {evalItem.last_run_pass_rate !== undefined &&
            evalItem.last_run_pass_rate !== null && (
              <>
                <span className="text-muted-foreground/50">·</span>
                <span
                  className={
                    evalItem.last_run_pass_rate >= 0.8
                      ? 'text-green-600'
                      : evalItem.last_run_pass_rate >= 0.5
                        ? 'text-amber-600'
                        : 'text-red-600'
                  }
                >
                  Pass rate: {Math.round(evalItem.last_run_pass_rate * 100)}%
                </span>
              </>
            )}
        </>
      ) : (
        <span>Never run</span>
      )}
    </div>
  );
}

export default EvalList;
